import React, { useState, useEffect, useCallback } from 'react';
import { flushSync } from 'react-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Alert,
  AlertTitle,
  CircularProgress,
  LinearProgress,
  Tabs,
  Tab,
  Tooltip,
  Divider,
  Collapse,
  Badge,
  Stack,
  Checkbox,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  PlayArrow as StartIcon,
  Stop as StopIcon,
  Refresh as RefreshIcon,
  AccountBalance as PayoutsIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CompletedIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Pending as PendingIcon,
  Cancel as CancelIcon,
  Replay as RetryIcon,
  Visibility as ViewIcon,
  MonetizationOn as MoneyIcon,
  Person as PersonIcon,
  Timeline as TimelineIcon,
  Dashboard as DashboardIcon,
  Info as InfoIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  SwapHoriz as SwapHorizIcon,
  Warning as WarningIcon,
  Notes as NotesIcon,
  Block as BlockIcon,
  Gavel as GavelIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ContentCopy as CopyIcon,
  Euro as EuroIcon,
  Loop as LoopIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useAuth } from '../../context/AuthContext';
import apiClient from '../../utils/apiClient';

/* Styled */

const KpiCard = styled(Card)(({ theme, color }) => ({
  background: color || theme.palette.background.paper,
  color: color ? theme.palette.common.white : 'inherit',
  transition: 'transform 0.2s',
  '&:hover': { transform: 'translateY(-3px)', boxShadow: theme.shadows[6] }
}));

const CurrencyBadge = styled(Chip)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '0.7rem',
  height: 20,
  borderRadius: 4
}));

const fmt = (amount, currency = 'EUR') =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency, minimumFractionDigits: 2 }).format(amount ?? 0);

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
};

const fmtDateShort = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const STATUS_CONFIG = {
  pending:             { label: 'En attente',     color: 'warning', icon: <PendingIcon fontSize="small" /> },
  processing:          { label: 'En traitement',  color: 'info',    icon: <LoopIcon fontSize="small" /> },
  completed:           { label: 'Complété',        color: 'success', icon: <CompletedIcon fontSize="small" /> },
  failed:              { label: 'Échec',           color: 'error',   icon: <ErrorIcon fontSize="small" /> },
  cancelled:           { label: 'Annulé',          color: 'default', icon: <CancelIcon fontSize="small" /> },
  deducted_for_penalty:{ label: 'Déduit (pén.)',  color: 'error',   icon: <GavelIcon fontSize="small" /> }
};

const PAYOUT_TYPE_CONFIG = {
  automatic:           { label: 'Automatique',        color: 'primary' },
  manual:              { label: 'Manuel',              color: 'secondary' },
  immediate:           { label: 'Immédiat',            color: 'warning' },
  damage_compensation: { label: 'Comp. dommages',     color: 'info' }
};

const StatusChip = ({ status, size = 'small' }) => {
  const cfg = STATUS_CONFIG[status] || { label: status, color: 'default', icon: <InfoIcon fontSize="small" /> };
  return <Chip icon={cfg.icon} label={cfg.label} color={cfg.color} size={size} sx={{ fontWeight: 600 }} />;
};

const TypeChip = ({ type }) => {
  const cfg = PAYOUT_TYPE_CONFIG[type] || { label: type, color: 'default' };
  return <Chip label={cfg.label} color={cfg.color} size="small" variant="outlined" />;
};

/**
 * Affiche le montant avec distinction devise originale / EUR
 */
const AmountCell = ({ payout }) => {
  const original = parseFloat(payout.amount ?? 0);
  const net = parseFloat(payout.net_amount ?? 0);
  const origCurrency = payout.original_currency || payout.currency || 'EUR';
  const payoutCurrency = payout.currency || 'EUR';
  const hasConversion = origCurrency !== payoutCurrency && parseFloat(payout.exchange_rate ?? 1) !== 1;

  return (
    <Box>
      <Typography variant="body2" fontWeight={700}>
        {fmt(net, payoutCurrency)}
      </Typography>
      {hasConversion && (
        <Stack direction="row" alignItems="center" spacing={0.5} mt={0.3}>
          <CurrencyBadge label={origCurrency} color="default" size="small" />
          <SwapHorizIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
          <CurrencyBadge label={payoutCurrency} color="primary" size="small" />
          <Typography variant="caption" color="text.secondary">
            ×{parseFloat(payout.exchange_rate).toFixed(4)}
          </Typography>
        </Stack>
      )}
      {payout.metadata?.dispute_refund_deducted && (
        <Tooltip title={`Remboursement litige déduit : ${fmt(payout.metadata.dispute_refund_amount)}`}>
          <Typography variant="caption" color="error.main" display="block">
            ⚠ remboursement déduit
          </Typography>
        </Tooltip>
      )}
    </Box>
  );
};

const PayoutFilterPanel = ({
  filters,
  filterOpen,
  loading,
  actionLoading,
  setFilters,
  setFilterOpen,
  resetFilters,
  loadPayouts,
  setPage
}) => {
  const hasActiveFilters = Object.values(filters).some((value) => value);

  return (
    <Paper sx={{ mb: 2, p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: filterOpen ? 2 : 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FilterIcon color="action" />
          <Typography variant="subtitle1" fontWeight={600}>Filtres</Typography>
          {hasActiveFilters && (
            <Badge badgeContent={Object.values(filters).filter((value) => value).length} color="primary">
              <Box />
            </Badge>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {hasActiveFilters && (
            <Button size="small" onClick={resetFilters}>Réinitialiser</Button>
          )}
          <IconButton size="small" onClick={() => setFilterOpen((previous) => !previous)}>
            {filterOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </Box>
      <Collapse in={filterOpen}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Recherche (propriétaire, prénom, email, ref...)"
              value={filters.search}
              onChange={(event) => {
                setPage(0);
                setFilters((previous) => ({ ...previous, search: event.target.value }));
              }}
              inputProps={{ autoComplete: 'off' }}
              InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} /> }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Statut</InputLabel>
              <Select
                value={filters.status}
                onChange={(event) => {
                  setFilters((previous) => ({ ...previous, status: event.target.value }));
                  setPage(0);
                }}
                label="Statut"
              >
                <MenuItem value="">Tous</MenuItem>
                {Object.entries(STATUS_CONFIG).map(([value, config]) => (
                  <MenuItem key={value} value={value}>{config.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={1}>
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select
                value={filters.payout_type}
                onChange={(event) => {
                  setFilters((previous) => ({ ...previous, payout_type: event.target.value }));
                  setPage(0);
                }}
                label="Type"
              >
                <MenuItem value="">Tous</MenuItem>
                {Object.entries(PAYOUT_TYPE_CONFIG).map(([value, config]) => (
                  <MenuItem key={value} value={value}>{config.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={1}>
            <TextField
              fullWidth
              size="small"
              label="ID Réservation"
              value={filters.reservation_id}
              onChange={(event) => {
                setPage(0);
                setFilters((previous) => ({ ...previous, reservation_id: event.target.value }));
              }}
              inputProps={{ autoComplete: 'off' }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Du"
              value={filters.dateFrom}
              onChange={(event) => {
                setFilters((previous) => ({ ...previous, dateFrom: event.target.value }));
                setPage(0);
              }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Au"
              value={filters.dateTo}
              onChange={(event) => {
                setFilters((previous) => ({ ...previous, dateTo: event.target.value }));
                setPage(0);
              }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={1}>
            <Button fullWidth variant="contained" sx={{ height: 40 }} onClick={loadPayouts} disabled={loading || actionLoading}>
              <SearchIcon />
            </Button>
          </Grid>
        </Grid>
      </Collapse>
    </Paper>
  );
};


const AdminPayoutsPage = () => {
  const { user } = useAuth();

  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const [payouts, setPayouts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState({});
  const [schedulerStatus, setSchedulerStatus] = useState({});

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filters, setFilters] = useState({
    status: '',
    payout_type: '',
    currency: '',
    dateFrom: '',
    dateTo: '',
    search: '',
    reservation_id: ''
  });
  const [filterOpen, setFilterOpen] = useState(true);

  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: '', data: null, note: '' });
  const [bulkNoteDialog, setBulkNoteDialog] = useState({ open: false, action: '', note: '' });
  const [actionLoading, setActionLoading] = useState(false);
  const [actionLabel, setActionLabel]     = useState('');
  const [bulkProgress, setBulkProgress]   = useState(null); // {current, total} | null
  const [expandedPayoutId, setExpandedPayoutId] = useState(null);
  const [notesDialog, setNotesDialog] = useState({ open: false, payout: null, note: '' });
  const [stripeErrorDialog, setStripeErrorDialog] = useState({ open: false, error: null, payout: null });
  
  const [selectedPayoutIds, setSelectedPayoutIds] = useState(new Set());
  const [bulkActionDialog, setBulkActionDialog] = useState({ open: false, action: '', count: 0 });


  const showSnackbar = useCallback((message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const loadPayouts = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filters.status)        params.append('status', filters.status);
      if (filters.payout_type)   params.append('payout_type', filters.payout_type);
      if (filters.currency)      params.append('currency', filters.currency);
      if (filters.dateFrom)      params.append('start_date', filters.dateFrom);
      if (filters.dateTo)        params.append('end_date', filters.dateTo);
      if (filters.search)        params.append('search', filters.search);
      if (filters.reservation_id) params.append('reservation_id', filters.reservation_id);
      params.append('page', page + 1);
      params.append('limit', rowsPerPage);

      const response = await apiClient.get(`/admin/payouts?${params}`);
      const data = response.data?.data || response.data || {};
      const rows = data.payouts || (Array.isArray(data) ? data : []);
      setPayouts(rows);
      setTotalCount(data.pagination?.total ?? rows.length);
    } catch (error) {
      console.error('Error loading payouts:', error);
      showSnackbar('Erreur lors du chargement des payouts', 'error');
      setPayouts([]);
    }
  }, [filters, page, rowsPerPage, showSnackbar]);

  const loadStats = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.payout_type) params.append('payout_type', filters.payout_type);
      if (filters.currency) params.append('currency', filters.currency);
      if (filters.search) params.append('search', filters.search);
      if (filters.reservation_id) params.append('reservation_id', filters.reservation_id);
      if (filters.dateFrom) params.append('start_date', filters.dateFrom);
      if (filters.dateTo) params.append('end_date', filters.dateTo);
      
      const response = await apiClient.get(`/admin/payouts/stats?${params.toString()}`);
      const statsData = response.data?.data?.stats || response.data?.stats || response.data?.data || {};
      setStats(statsData);
    } catch (error) {
      console.error('Error loading stats:', error);
      setStats({});
    }
  }, [filters]);

  const loadSchedulerStatus = useCallback(async () => {
    try {
      const response = await apiClient.get('/admin/scheduler/status');
      setSchedulerStatus(response.data?.data || response.data || {});
    } catch (error) {
      console.error('Error loading scheduler:', error);
      setSchedulerStatus({});
    }
  }, []);

  const refreshPayoutViews = useCallback(async () => {
    await Promise.all([loadPayouts(), loadStats()]);
  }, [loadPayouts, loadStats]);

  const loadAllData = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([loadPayouts(), loadStats(), loadSchedulerStatus()]);
    } finally {
      setLoading(false);
    }
  }, [loadPayouts, loadStats, loadSchedulerStatus]);

  useEffect(() => { loadAllData(); }, []);
  useEffect(() => { loadPayouts(); }, [loadPayouts]);

 
  const handleSchedulerAction = async (action) => {
    try {
      setLoading(true);
      await apiClient.post(`/admin/scheduler/${action}`);
      await loadSchedulerStatus();
      showSnackbar(`Scheduler ${action === 'start' ? 'démarré' : action === 'stop' ? 'arrêté' : 'redémarré'}`, 'success');
    } catch (error) {
      showSnackbar(`Erreur scheduler: ${error.response?.data?.message || error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleManualRun = async () => {
    try {
      setLoading(true);
      await apiClient.post('/admin/scheduler/run');
      showSnackbar('Traitement manuel initié', 'success');
      setTimeout(() => loadAllData(), 3000);
    } catch (error) {
      showSnackbar(`Erreur traitement: ${error.response?.data?.message || error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handlePayoutAction = async (action, payoutId, payoutData = null, adminNote = '') => {
    const label = action === 'retry' ? 'Relance du payout…' : action === 'force' ? 'Forçage du payout…' : 'Annulation du payout…';
    // flushSync guarantees React renders the loading bar BEFORE the async API call
    flushSync(() => {
      setActionLoading(true);
      setActionLabel(label);
      setBulkProgress(null);
    });
    try {
      const endpoints = { retry: 'put', cancel: 'put', force: 'post' };
      const method = endpoints[action] || 'post';
      const body = { ...(payoutData ? { currency: payoutData.currency } : {}), admin_note: adminNote, reason: adminNote };
      const res = await apiClient[method](`/admin/payouts/${payoutId}/${action}`, body);
      if (action === 'retry') {
        showSnackbar('Payout relancé. Il sera traité lors du prochain cycle cron.', 'success');
      } else {
        showSnackbar(
          action === 'cancel' ? 'Payout annulé.' : `Payout forcé — Transfer: ${res.data?.data?.transfer_id || 'OK'}`,
          'success'
        );
      }
    } catch (error) {
      if (error.response?.data?.error_code === 'STRIPE_CONNECT_ACCOUNT_NOT_READY') {
        setStripeErrorDialog({ open: true, error: error.response.data, payout: payoutData });
      } else {
        showSnackbar(`Erreur: ${error.response?.data?.message || error.message}`, 'error');
      }
    } finally {
      // Always refresh data whether action succeeded or failed
      setActionLabel('Actualisation des données…');
      await refreshPayoutViews();
      setActionLoading(false);
      setActionLabel('');
    }
  };

  const handleAddNotes = async () => {
    if (!notesDialog.note.trim()) return;
    try {
      await apiClient.put(`/admin/payouts/${notesDialog.payout.id}/admin-notes`, { notes: notesDialog.note });
      showSnackbar('Note ajoutée', 'success');
      setNotesDialog({ open: false, payout: null, note: '' });
      await loadPayouts();
    } catch (error) {
      showSnackbar(`Erreur: ${error.response?.data?.message || error.message}`, 'error');
    }
  };

  const handleOpenDetail = (payoutId) => {
    setExpandedPayoutId(expandedPayoutId === payoutId ? null : payoutId);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard?.writeText(text);
    showSnackbar('Copié !', 'info');
  };

  const resetFilters = () => {
    setFilters({ status: '', payout_type: '', currency: '', dateFrom: '', dateTo: '', search: '', reservation_id: '' });
    setPage(0);
  };

  /* ── Selection & Bulk Actions ───────────────────────────────────────────────── */

  const toggleSelectPayout = (id) => {
    setSelectedPayoutIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const selectAllPayouts = () => {
    if (selectedPayoutIds.size === payouts.length && payouts.length > 0) {
      setSelectedPayoutIds(new Set());
    } else if (payouts.length > 0) {
      const allIds = new Set(payouts.map(p => p.id));
      setSelectedPayoutIds(allIds);
    }
  };

  const handleBulkAction = async (action, note = '') => {
    if (selectedPayoutIds.size === 0) {
      showSnackbar('Aucun payout sélectionné', 'warning');
      return;
    }

    const selectedPayoutsData = payouts.filter(p => selectedPayoutIds.has(p.id));
    const payoutIdsList = Array.from(selectedPayoutIds);
    const bulkLabel = action === 'retry' ? 'Relance groupée…' : action === 'force' ? 'Forçage groupé…' : 'Annulation groupée…';

    flushSync(() => {
      setActionLoading(true);
      setActionLabel(bulkLabel);
      setBulkProgress({ current: 0, total: payoutIdsList.length });
    });

    try {
      
      // Vérifier que l'action est valide pour tous les payouts sélectionnés
      let isValid = true;
      let errorMsg = '';

      if (action === 'retry') {
        isValid = selectedPayoutsData.every(p => p.status === 'failed');
        errorMsg = 'Seuls les payouts en échec peuvent être relancés';
      } else if (action === 'force') {
        isValid = selectedPayoutsData.every(p => ['pending', 'failed', 'cancelled'].includes(p.status));
        errorMsg = 'Seuls les payouts pending/failed/cancelled peuvent être forcés';
      } else if (action === 'cancel') {
        isValid = selectedPayoutsData.every(p => ['pending', 'processing'].includes(p.status));
        errorMsg = 'Seuls les payouts pending/processing peuvent être annulés';
      }

      if (!isValid) {
        showSnackbar(errorMsg, 'error');
        setBulkActionDialog({ open: false, action: '', count: 0 });
        return;
      }

      // Effectuer l'action groupée
      let successCount = 0;
      let errorCount = 0;

      for (const [index, payoutId] of payoutIdsList.entries()) {
        try {
          if (action === 'retry') {
            await apiClient.put(`/admin/payouts/${payoutId}/retry`, { admin_note: note });
          } else if (action === 'force') {
            await apiClient.post(`/admin/payouts/${payoutId}/force`, { admin_note: note });
          } else if (action === 'cancel') {
            await apiClient.put(`/admin/payouts/${payoutId}/cancel`, { reason: note, admin_note: note });
          }
          successCount++;
        } catch (err) {
          errorCount++;
          console.error(`Failed to ${action} payout ${payoutId}`, err);
        }
        setBulkProgress({ current: index + 1, total: payoutIdsList.length });
      }

      const resultLabel = action === 'retry' ? 'Relancé' : action === 'force' ? 'Forcé' : 'Annulé';
      showSnackbar(`${resultLabel}: ${successCount}/${payoutIdsList.length}`, errorCount > 0 ? 'warning' : 'success');
      setSelectedPayoutIds(new Set());

    } catch (error) {
      showSnackbar(`Erreur lors de l'action groupée: ${error.message}`, 'error');
    } finally {
      // Always refresh data whether actions succeeded or failed
      setActionLabel('Actualisation des données…');
      setBulkProgress(null);
      await refreshPayoutViews();
      setActionLoading(false);
      setActionLabel('');
    }
  };

  /* ── KPI Cards ──────────────────────────────────────────────────────────────── */

  const KpiSection = () => {
    const completed  = stats.byStatus?.completed  || {};
    const pending    = stats.byStatus?.pending    || {};
    const processing = stats.byStatus?.processing || {};
    const failed     = stats.byStatus?.failed     || {};

    return (
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <KpiCard color="#1565c0">
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="overline" sx={{ opacity: 0.8, color: 'inherit' }}>Complétés</Typography>
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 700 }}>{completed.count ?? 0}</Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>{fmt(completed.amount)}</Typography>
                </Box>
                <CompletedIcon sx={{ fontSize: 48, opacity: 0.4, color: 'white' }} />
              </Box>
            </CardContent>
          </KpiCard>
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <KpiCard color="#e65100">
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="overline" sx={{ opacity: 0.8, color: 'inherit' }}>Échecs</Typography>
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 700 }}>{failed.count ?? 0}</Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>{fmt(failed.amount)}</Typography>
                </Box>
                <ErrorIcon sx={{ fontSize: 48, opacity: 0.4, color: 'white' }} />
              </Box>
            </CardContent>
          </KpiCard>
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <KpiCard color="#f57f17">
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="overline" sx={{ opacity: 0.8, color: 'inherit' }}>En attente</Typography>
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 700 }}>{(pending.count ?? 0) + (processing.count ?? 0)}</Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>{fmt((pending.amount ?? 0) + (processing.amount ?? 0))}</Typography>
                </Box>
                <PendingIcon sx={{ fontSize: 48, opacity: 0.4, color: 'white' }} />
              </Box>
            </CardContent>
          </KpiCard>
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <KpiCard>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="overline" color="text.secondary">Total</Typography>
                  <Typography variant="h4" fontWeight={700}>{stats.totalPayouts ?? 0}</Typography>
                  <Typography variant="body2" color="text.secondary">{fmt(stats.totalAmount)}</Typography>
                </Box>
                <PayoutsIcon sx={{ fontSize: 48, opacity: 0.15 }} />
              </Box>
              {/* Scheduler status inline */}
              <Box sx={{ mt: 1 }}>
                <Chip
                  size="small"
                  label={schedulerStatus.scheduler?.isRunning ? 'Scheduler actif' : 'Scheduler inactif'}
                  color={schedulerStatus.scheduler?.isRunning ? 'success' : 'error'}
                />
              </Box>
            </CardContent>
          </KpiCard>
        </Grid>
      </Grid>
    );
  };

  /* ── Payout Table ───────────────────────────────────────────────────────────── */

  const ExpandedPayoutDetail = ({ payout: p }) => {
    if (!p) return null;

    const origCurrency = p.original_currency || p.currency || 'EUR';
    const payoutCurrency = p.currency || 'EUR';
    const hasConversion = origCurrency !== payoutCurrency;
    const meta = p.metadata || {};
    const calc = meta.calculation_details || {};
    const conv = meta.currency_conversion || {};
    const adminNotes = meta.admin_notes || '';
    const adminActionLog = Array.isArray(meta.admin_action_log) ? meta.admin_action_log : [];
    const isRowSelected = selectedPayoutIds.has(p.id);

    const DetailRow = ({ label, value, mono = false, color }) => (
      <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.7, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0, mr: 2 }}>{label}</Typography>
        <Typography variant="body2" fontWeight={600} fontFamily={mono ? 'monospace' : 'inherit'} color={color} sx={{ textAlign: 'right', wordBreak: 'break-all' }}>
          {value ?? '—'}
        </Typography>
      </Box>
    );

    return (
      <Box sx={{ backgroundColor: 'white', borderRadius: 1, p: 2 }}>
        {/* Échec */}
        {p.failure_reason && (
          <Alert severity="error" icon={<ErrorIcon />} sx={{ mb: 2 }}>
            <AlertTitle>Raison de l'échec</AlertTitle>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
              {p.failure_reason}
            </Typography>
            {meta.failed_at && (
              <Typography variant="caption" color="error.light">
                Survenu le {fmtDate(meta.failed_at)}
              </Typography>
            )}
          </Alert>
        )}

        {/* Pénalité */}
        {p.status === 'deducted_for_penalty' && (
          <Alert severity="warning" icon={<GavelIcon />} sx={{ mb: 2 }}>
            <AlertTitle>Payout déduit pour pénalité</AlertTitle>
            Ce montant a été entièrement retenu comme pénalité.
          </Alert>
        )}

        {/* Propriétaire */}
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>👤 Propriétaire</Typography>
            <DetailRow label="Nom" value={p.owner?.name || '—'} />
            <DetailRow label="Prénom" value={p.owner?.first_name || p.owner?.name?.split(' ')[0] || '—'} />
            <DetailRow label="Email" value={p.owner?.email || '—'} mono />
            <DetailRow label="Compte Stripe" value={p.stripe_account_id || '—'} mono />

            {p.owner ? (
              <Box
                sx={{
                  mt: 2,
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: (p.owner.stripe_onboarding_status === 'completed' && p.owner.stripe_connect_status === 'enabled') ? 'success.main' : 'warning.main',
                  color: 'white'
                }}
              >
                <Typography variant="body2" fontWeight={700}>
                  Statut Stripe : {p.owner.stripe_onboarding_status || 'inconnu'} / {p.owner.stripe_connect_status || 'inconnu'}
                </Typography>
                <Typography variant="caption">
                  {p.owner.name ? `${p.owner.name}` : 'Nom non disponible'} • {p.owner.email ? `${p.owner.email}` : 'Email non disponible'}
                </Typography>
              </Box>
            ) : (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
                Informations propriétaire manquantes
              </Typography>
            )}
          </Grid>

          {/* Réservation */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>📅 Réservation</Typography>
            <DetailRow label="ID" value={p.reservation_id} mono />
            <DetailRow label="Ref. paiement" value={p.reservation?.payment_reference} mono />
            <DetailRow label="Dates" value={p.reservation ? `${fmtDateShort(p.reservation.start_date)} → ${fmtDateShort(p.reservation.end_date)}` : '—'} />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Montants */}
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>💰 Montants</Typography>
        <Grid container spacing={2} sx={{ backgroundColor: 'grey.0', p: 1.5, borderRadius: 1 }}>
          <Grid item xs={12} sm={6}>
            <DetailRow label="Montant brut" value={fmt(parseFloat(p.amount ?? 0), origCurrency)} />
            <DetailRow label="Commission" value={calc.commission ? fmt(parseFloat(calc.commission), payoutCurrency) : '—'} />
            <DetailRow label="Taxes / Frais" value={calc.taxes ? fmt(parseFloat(calc.taxes), payoutCurrency) : '—'} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <DetailRow label="Mnt net(€)" value={fmt(parseFloat(p.net_amount ?? 0), payoutCurrency)} color="primary.main" />
            {hasConversion && (
              <>
                <DetailRow label="Devise orig." value={origCurrency} />
                <DetailRow label="Taux de change" value={`${parseFloat(p.exchange_rate ?? 1).toFixed(4)} (1 ${origCurrency} = ${parseFloat(p.exchange_rate ?? 1).toFixed(4)} EUR)`} mono />
              </>
            )}
          </Grid>
        </Grid>

        {Array.isArray(meta.penalty_deductions) && meta.penalty_deductions.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>⚖️ Pénalités déduites</Typography>
            {meta.penalty_deductions.map((ded, idx) => (
              <Box key={idx} sx={{ mb: 1, p: 1.5, borderRadius: 1, border: '1px solid', borderColor: 'warning.light', backgroundColor: 'rgba(255,152,0,0.05)' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="body2" fontWeight={700} color="error.main">
                    − {fmt(parseFloat(ded.deducted_amount || 0), payoutCurrency)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">{fmtDate(ded.date)}</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>{ded.reason || '—'}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {fmt(parseFloat(ded.previous_net_amount || 0), payoutCurrency)} → {fmt(parseFloat(ded.new_net_amount || 0), payoutCurrency)}
                </Typography>
              </Box>
            ))}
            <Box sx={{ mt: 1, p: 1, borderRadius: 1, bgcolor: 'error.light' }}>
              <Typography variant="body2" fontWeight={700} color="error.dark">
                Total pénalités : − {fmt(meta.penalty_deductions.reduce((s, d) => s + parseFloat(d.deducted_amount || 0), 0), payoutCurrency)}
              </Typography>
            </Box>
          </>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Transfert Stripe */}
        {p.stripe_transfer_id && (
          <>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>🔄 Transfert Stripe</Typography>
            <DetailRow 
              label="ID du transfert" 
              value={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <span style={{ fontFamily: 'monospace', fontSize: '0.85em' }}>{p.stripe_transfer_id.slice(0, 20)}...</span>
                  <IconButton size="small" onClick={() => copyToClipboard(p.stripe_transfer_id)}><CopyIcon fontSize="small" /></IconButton>
                </Box>
              }
              mono
            />
            <DetailRow label="Statut" value={<StatusChip status={p.status} />} />
            {p.processed_at && <DetailRow label="Traité le" value={fmtDate(p.processed_at)} />}
            <Divider sx={{ my: 2 }} />
          </>
        )}

        {/* Dates */}
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>📆 Dates</Typography>
        <DetailRow label="Créé le" value={fmtDate(p.created_at)} />
        <DetailRow label="Date prévue" value={fmtDateShort(p.scheduled_date)} />
        {p.processed_at && <DetailRow label="Traité le" value={fmtDate(p.processed_at)} />}

        {/* Admin Notes */}
        {adminNotes && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>📝 Notes admin</Typography>
            <Typography variant="body2" sx={{ p: 1.5, backgroundColor: 'info.light', borderRadius: 1, color: 'info.dark' }}>
              {adminNotes}
            </Typography>
            {meta.admin_notes_history && meta.admin_notes_history.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">Historique:</Typography>
                {meta.admin_notes_history.map((note, idx) => (
                  <Typography key={idx} variant="caption" display="block" sx={{ mt: 0.5, fontStyle: 'italic', color: 'text.secondary' }}>
                    {fmtDate(note.timestamp)}: {note.text}
                  </Typography>
                ))}
              </Box>
            )}
          </>
        )}

        {/* Journal des actions admin */}
        {adminActionLog.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>🔐 Journal des actions admin</Typography>
            {adminActionLog.map((entry, idx) => {
              const actionConfig = {
                cancel: { label: 'Annulation', color: 'error' },
                force:  { label: 'Forcé',      color: 'warning' },
                retry:  { label: 'Relancement', color: 'info' }
              };
              const cfg = actionConfig[entry.action] || { label: entry.action, color: 'default' };
              return (
                <Box key={idx} sx={{ mb: 1.5, p: 1.5, borderRadius: 1, border: '1px solid', borderColor: `${cfg.color}.light`, backgroundColor: cfg.color === 'error' ? 'rgba(229,57,53,0.05)' : cfg.color === 'warning' ? 'rgba(255,152,0,0.05)' : 'rgba(2,136,209,0.05)' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Chip size="small" label={cfg.label} color={cfg.color} />
                    <Typography variant="caption" color="text.secondary">{fmtDate(entry.timestamp)}</Typography>
                  </Box>
                  <Typography variant="body2" fontWeight={600}>{entry.admin_name}{entry.admin_email ? ` — ${entry.admin_email}` : ''}</Typography>
                  <Typography variant="body2" sx={{ mt: 0.5, fontStyle: 'italic', color: 'text.primary', whiteSpace: 'pre-wrap', p: 1, backgroundColor: 'background.paper', borderRadius: 0.5, mt: 0.75 }}>
                    {entry.note || '—'}
                  </Typography>
                </Box>
              );
            })}
          </>
        )}

        {/* Boutons d'action */}
        <Box sx={{ display: 'flex', gap: 1, mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          {selectedPayoutIds.size > 0 && isRowSelected ? (
            <Box sx={{ 
              p: 2, 
              backgroundColor: 'info.light', 
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'info.main',
              width: '100%',
              textAlign: 'center'
            }}>
              <Typography variant="body2" color="info.dark" fontWeight={600}>
                🔒 Actions désactivées — Utilisez la barre d'actions groupées pour continuer
              </Typography>
            </Box>
          ) : (
            <>
              <Button size="small" variant="outlined" onClick={() => setNotesDialog({ open: true, payout: p, note: '' })} disabled={selectedPayoutIds.size > 0}>
                <NotesIcon sx={{ mr: 0.5 }} fontSize="small" /> Ajouter une note
              </Button>
              {p.status === 'failed' && (
                <Button size="small" variant="outlined" color="warning" onClick={() => setConfirmDialog({ open: true, action: 'retry', data: p, note: '' })} disabled={selectedPayoutIds.size > 0}>
                  <RetryIcon sx={{ mr: 0.5 }} fontSize="small" /> Relancer
                </Button>
              )}
              {['pending', 'failed', 'cancelled'].includes(p.status) && (
                <Button size="small" variant="contained" color="primary" onClick={() => setConfirmDialog({ open: true, action: 'force', data: p, note: '' })} disabled={selectedPayoutIds.size > 0}>
                  <StartIcon sx={{ mr: 0.5 }} fontSize="small" /> Forcer maintenant
                </Button>
              )}
              {['pending', 'processing'].includes(p.status) && (
                <Button size="small" variant="outlined" color="error" onClick={() => setConfirmDialog({ open: true, action: 'cancel', data: p, note: '' })} disabled={selectedPayoutIds.size > 0}>
                  <CancelIcon sx={{ mr: 0.5 }} fontSize="small" /> Annuler
                </Button>
              )}
            </>
          )}
        </Box>
      </Box>
    );
  };

  const PayoutsTable = () => (
    <Paper>
      {/* Bulk Actions Bar */}
      {selectedPayoutIds.size > 0 && (
        <Box sx={{ p: 2, backgroundColor: 'primary.light', borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon color="primary" />
            <Typography variant="subtitle2" fontWeight={700}>
              {selectedPayoutIds.size} payout{selectedPayoutIds.size > 1 ? 's' : ''} sélectionné{selectedPayoutIds.size > 1 ? 's' : ''}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" variant="outlined" onClick={() => setSelectedPayoutIds(new Set())}>
              Désélectionner
            </Button>
            {Array.from(selectedPayoutIds).some(id => payouts.find(p => p.id === id && p.status === 'failed')) && (
              <Button size="small" variant="contained" color="warning" onClick={() => setBulkNoteDialog({ open: true, action: 'retry', note: '' })} disabled={loading || actionLoading}>
                Relancer ({payouts.filter(p => selectedPayoutIds.has(p.id) && p.status === 'failed').length})
              </Button>
            )}
            {Array.from(selectedPayoutIds).some(id => payouts.find(p => p.id === id && ['pending', 'failed', 'cancelled'].includes(p.status))) && (
              <Button size="small" variant="contained" color="primary" onClick={() => setBulkNoteDialog({ open: true, action: 'force', note: '' })} disabled={loading || actionLoading}>
                Forcer ({payouts.filter(p => selectedPayoutIds.has(p.id) && ['pending', 'failed', 'cancelled'].includes(p.status)).length})
              </Button>
            )}
            {Array.from(selectedPayoutIds).some(id => payouts.find(p => p.id === id && ['pending', 'processing'].includes(p.status))) && (
              <Button size="small" variant="contained" color="error" onClick={() => setBulkNoteDialog({ open: true, action: 'cancel', note: '' })} disabled={loading || actionLoading}>
                Annuler ({payouts.filter(p => selectedPayoutIds.has(p.id) && ['pending', 'processing'].includes(p.status)).length})
              </Button>
            )}
          </Box>
        </Box>
      )}
      <TableContainer sx={{ width: '100%', minWidth: 1400 }}>
        <Table size="small" sx={{ width: '100%' }}>
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 700, backgroundColor: 'grey.50' } }}>
              <TableCell width={40} padding="checkbox">
                <Checkbox
                  indeterminate={selectedPayoutIds.size > 0 && selectedPayoutIds.size < payouts.length}
                  checked={payouts.length > 0 && selectedPayoutIds.size === payouts.length}
                  onChange={selectAllPayouts}
                />
              </TableCell>
              <TableCell>Propriétaire</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Mnt net(€)</TableCell>
              <TableCell>Devise orig. → EUR</TableCell>
              <TableCell>Statut</TableCell>
              <TableCell>Raison échec</TableCell>
              <TableCell>Date prévue</TableCell>
              <TableCell>Créé le</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {payouts.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                  Aucun payout trouvé
                </TableCell>
              </TableRow>
            )}
            {payouts.map((payout) => {
              const origCurrency = payout.original_currency || payout.currency || 'EUR';
              const payoutCurrency = payout.currency || 'EUR';
              const hasConversion = origCurrency !== payoutCurrency;
              const isPenalty = payout.status === 'deducted_for_penalty';
              const isFailed = payout.status === 'failed';

              return (
                <>
                  <TableRow
                    key={payout.id}
                    sx={{
                      backgroundColor: isFailed ? 'rgba(229,57,53,0.04)' : isPenalty ? 'rgba(156,39,176,0.04)' : 'inherit',
                      '&:hover': { backgroundColor: 'action.hover' },
                      cursor: 'pointer',
                      ...(expandedPayoutId === payout.id ? {
                        borderTop: '2px solid',
                        borderLeft: '2px solid',
                        borderRight: '2px solid',
                        borderColor: 'primary.main',
                        borderTopLeftRadius: '6px',
                        borderTopRightRadius: '6px'
                      } : {})
                    }}
                    onClick={() => handleOpenDetail(payout.id)}
                  >
                    {/* Checkbox */}
                    <TableCell padding="checkbox" width={40}>
                      <Checkbox
                        checked={selectedPayoutIds.has(payout.id)}
                        onChange={() => toggleSelectPayout(payout.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </TableCell>
                    
                    {/* Propriétaire */}
                    <TableCell>
                      {payout.owner ? (
                        <Box
                          sx={{
                            p: 0.5,
                            borderRadius: 1,
                            backgroundColor: payout.owner.stripe_onboarding_status === 'completed' && payout.owner.stripe_connect_status === 'enabled' ? 'success.main' : 'warning.main',
                            color: 'white'
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <PersonIcon fontSize="small" sx={{ color: 'inherit' }} />
                            <Box>
                              <Typography variant="body2" fontWeight={600} color="inherit">
                                {payout.owner.name || payout.owner.email || '—'}
                              </Typography>
                              {payout.owner.email && payout.owner.name && (
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                                  {payout.owner.email}
                                </Typography>
                              )}                              
                            </Box>
                          </Box>
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary">Informations propriétaire manquantes</Typography>
                      )}
                    </TableCell>

                    {/* Type */}
                    <TableCell>
                      <TypeChip type={payout.payout_type} />
                      {isPenalty && (
                        <Tooltip title="Ce payout a été entièrement déduit pour pénalité">
                          <GavelIcon fontSize="small" color="error" sx={{ ml: 0.5, verticalAlign: 'middle' }} />
                        </Tooltip>
                      )}
                    </TableCell>

                    {/* Montant net EUR */}
                    <TableCell>
                      <AmountCell payout={payout} />
                    </TableCell>

                    {/* Devise originale → EUR */}
                    <TableCell>
                      {hasConversion ? (
                        <Box>
                          <Typography variant="body2">
                            {fmt(parseFloat(payout.amount ?? 0), origCurrency)}
                          </Typography>
                          <Stack direction="row" alignItems="center" spacing={0.3}>
                            <Typography variant="caption" color="text.secondary">{origCurrency}</Typography>
                            <SwapHorizIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                            <Typography variant="caption" color="primary.main" fontWeight={700}>{payoutCurrency}</Typography>
                          </Stack>
                          <Typography variant="caption" color="text.secondary">
                            Taux: {parseFloat(payout.exchange_rate ?? 1).toFixed(4)}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">—</Typography>
                      )}
                    </TableCell>

                    {/* Statut */}
                    <TableCell>
                      <StatusChip status={payout.status} />
                      {payout.metadata?.below_minimum_threshold && (
                        <Tooltip title="Montant inférieur au seuil minimum">
                          <Chip label="< seuil" size="small" color="warning" variant="outlined" sx={{ mt: 0.5, display: 'block', width: 'fit-content' }} />
                        </Tooltip>
                      )}
                    </TableCell>

                    {/* Raison échec */}
                    <TableCell sx={{ maxWidth: 200 }}>
                      {payout.failure_reason ? (
                        <Tooltip title={payout.failure_reason}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                            <ErrorIcon fontSize="small" color="error" sx={{ flexShrink: 0, mt: 0.2 }} />
                            <Typography variant="caption" color="error.main" sx={{
                              display: '-webkit-box', WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical', overflow: 'hidden'
                            }}>
                              {`${payout.failure_reason.slice(0, 21)}${payout.failure_reason.length > 21 ? '...' : ''}`}
                            </Typography>
                          </Box>
                        </Tooltip>
                      ) : payout.status === 'cancelled' && payout.metadata?.cancelled_reason ? (
                        <Tooltip title={payout.metadata.cancelled_reason}>
                          <Typography variant="caption" color="text.secondary">
                            {`${payout.metadata.cancelled_reason.slice(0, 21)}${payout.metadata.cancelled_reason.length > 21 ? '...' : ''}`}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="caption" color="text.disabled">—</Typography>
                      )}
                    </TableCell>

                    {/* Date prévue */}
                    <TableCell>
                      <Typography variant="body2">{fmtDateShort(payout.scheduled_date)}</Typography>
                      {payout.processed_at && (
                        <Typography variant="caption" color="success.main" display="block">
                          Traité: {fmtDateShort(payout.processed_at)}
                        </Typography>
                      )}
                    </TableCell>

                    {/* Créé le */}
                    <TableCell>
                      <Typography variant="body2">{fmtDateShort(payout.created_at)}</Typography>
                    </TableCell>

                    {/* Actions */}
                    <TableCell align="right">
                      {selectedPayoutIds.size > 0 && selectedPayoutIds.has(payout.id) ? (
                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          Action groupée en cours...
                        </Typography>
                      ) : (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, alignItems: 'center' }}>
                          <Tooltip title={expandedPayoutId === payout.id ? "Réduire les détails" : "Afficher les détails"}>
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenDetail(payout.id); }} disabled={selectedPayoutIds.size > 0}>
                              {expandedPayoutId === payout.id ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Ajouter une note">
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); setNotesDialog({ open: true, payout, note: '' }); }} disabled={selectedPayoutIds.size > 0}>
                              <NotesIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {payout.status === 'failed' && (
                            <Tooltip title="Repasser en pending (retry au prochain cron)">
                                <IconButton size="small" color="warning"
                                  onClick={(e) => { e.stopPropagation(); setConfirmDialog({ open: true, action: 'retry', data: payout, note: '' }); }} disabled={loading || actionLoading || selectedPayoutIds.size > 0}>
                                  <RetryIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {['pending', 'failed', 'cancelled'].includes(payout.status) && (
                            <Tooltip title="Forcer l'exécution maintenant (Stripe)">
                              <IconButton size="small" color="primary"
                                onClick={(e) => { e.stopPropagation(); setConfirmDialog({ open: true, action: 'force', data: payout, note: '' }); }} disabled={loading || actionLoading || selectedPayoutIds.size > 0}>
                                <StartIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {['pending', 'processing'].includes(payout.status) && (
                            <Tooltip title="Annuler">
                              <IconButton size="small" color="error"
                                onClick={(e) => { e.stopPropagation(); setConfirmDialog({ open: true, action: 'cancel', data: payout, note: '' }); }} disabled={loading || actionLoading || selectedPayoutIds.size > 0}>
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                  
                  {/* Expansion Row - Détails */}
                  {expandedPayoutId === payout.id && (
                    <TableRow sx={{ backgroundColor: 'grey.50' }}>
                      <TableCell
                        colSpan={10}
                        sx={{
                          p: 2,
                          borderLeft: '2px solid',
                          borderRight: '2px solid',
                          borderBottom: '2px solid',
                          borderColor: 'primary.main',
                          borderBottomLeftRadius: '6px',
                          borderBottomRightRadius: '6px'
                        }}
                      >
                        <ExpandedPayoutDetail payout={payout} />
                      </TableCell>
                    </TableRow>
                  )}
                </>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        rowsPerPageOptions={[10, 25, 50, 100]}
        labelRowsPerPage="Lignes par page"
        labelDisplayedRows={({ from, to, count }) => `${from}–${to} sur ${count !== -1 ? count : `+${to}`}`}
      />
    </Paper>
  );

  /* ── Analytics Tab ──────────────────────────────────────────────────────────── */

  const AnalyticsTab = () => (
    <Grid container spacing={3}>
      {/* Par statut */}
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>Répartition par statut</Typography>
          {stats.byStatus && Object.entries(stats.byStatus).map(([status, data]) => (
            <Box key={status} sx={{ mb: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <StatusChip status={status} size="small" />
                <Typography variant="body2" fontWeight={600}>
                  {data.count} — {fmt(data.amount)}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={Math.min(100, ((data.count ?? 0) / (stats.totalPayouts || 1)) * 100)}
                sx={{ height: 6, borderRadius: 3 }}
                color={STATUS_CONFIG[status]?.color || 'primary'}
              />
            </Box>
          ))}
          {!stats.byStatus && <Typography color="text.secondary">Aucune donnée</Typography>}
        </Paper>
      </Grid>

      {/* Par devise */}
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>Répartition par devise</Typography>
          {stats.byCurrency && Object.entries(stats.byCurrency).map(([currency, data]) => (
            <Box key={currency} sx={{ mb: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <EuroIcon fontSize="small" color="action" />
                  <Typography variant="body2" fontWeight={600}>{currency}</Typography>
                </Box>
                <Typography variant="body2">{data.count} payouts — {fmt(data.amount, currency)}</Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={Math.min(100, ((data.count ?? 0) / (stats.totalPayouts || 1)) * 100)}
                sx={{ height: 6, borderRadius: 3 }}
                color="secondary"
              />
            </Box>
          ))}
          {!stats.byCurrency && <Typography color="text.secondary">Aucune donnée</Typography>}
        </Paper>
      </Grid>

      {/* Taux d'échec */}
      <Grid item xs={12} md={4}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>Taux d'échec</Typography>
          {stats.totalPayouts > 0 ? (
            <>
              <Typography variant="h4" color="error.main" fontWeight={700}>
                {(((stats.byStatus?.failed?.count ?? 0) / stats.totalPayouts) * 100).toFixed(1)} %
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {stats.byStatus?.failed?.count ?? 0} échecs sur {stats.totalPayouts} payouts
              </Typography>
              <LinearProgress
                variant="determinate"
                value={Math.min(100, ((stats.byStatus?.failed?.count ?? 0) / stats.totalPayouts) * 100)}
                sx={{ height: 8, borderRadius: 4, mt: 1 }}
                color="error"
              />
            </>
          ) : <Typography color="text.secondary">Pas de données</Typography>}
        </Paper>
      </Grid>

      {/* Taux de succès */}
      <Grid item xs={12} md={4}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>Taux de succès</Typography>
          {stats.totalPayouts > 0 ? (
            <>
              <Typography variant="h4" color="success.main" fontWeight={700}>
                {(((stats.byStatus?.completed?.count ?? 0) / stats.totalPayouts) * 100).toFixed(1)} %
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {stats.byStatus?.completed?.count ?? 0} complétés sur {stats.totalPayouts}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={Math.min(100, ((stats.byStatus?.completed?.count ?? 0) / stats.totalPayouts) * 100)}
                sx={{ height: 8, borderRadius: 4, mt: 1 }}
                color="success"
              />
            </>
          ) : <Typography color="text.secondary">Pas de données</Typography>}
        </Paper>
      </Grid>

      {/* Scheduler santé */}
      <Grid item xs={12} md={4}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>Santé du scheduler</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Chip
              label={schedulerStatus.scheduler?.isRunning ? 'ACTIF' : 'INACTIF'}
              color={schedulerStatus.scheduler?.isRunning ? 'success' : 'error'}
              sx={{ width: 'fit-content', fontWeight: 700 }}
            />
            <Typography variant="body2" color="text.secondary">
              Fréquence : {schedulerStatus.scheduler?.frequency || 'N/A'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Payouts en attente : {schedulerStatus.pendingPayouts?.count ?? 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Montant en attente : {fmt(schedulerStatus.pendingPayouts?.totalAmount)}
            </Typography>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );

  /* ── Render ─────────────────────────────────────────────────────────────────── */

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
            <PayoutsIcon sx={{ fontSize: 36, color: 'primary.main' }} />
            Gestion des Payouts
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Transferts automatiques aux propriétaires via Stripe Connect
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadAllData} disabled={loading || actionLoading}>
            Actualiser
          </Button>
          <Button variant="outlined" startIcon={schedulerStatus.scheduler?.isRunning ? <StopIcon /> : <StartIcon />}
            color={schedulerStatus.scheduler?.isRunning ? 'error' : 'success'}
            onClick={() => handleSchedulerAction(schedulerStatus.scheduler?.isRunning ? 'stop' : 'start')}
            disabled={loading || actionLoading}>
            {schedulerStatus.scheduler?.isRunning ? 'Arrêter' : 'Démarrer'} scheduler
          </Button>
          <Button variant="contained" startIcon={<StartIcon />} onClick={handleManualRun} disabled={loading || actionLoading}>
            Exécution manuelle
          </Button>
        </Box>
      </Box>

      {/* ── Action progress bar — fixed at top so always visible ── */}
      {actionLoading && (
        <Box sx={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 2000,
          backgroundColor: 'primary.dark',
          display: 'flex', alignItems: 'center', gap: 2, px: 3, py: 0.75,
          boxShadow: 4
        }}>
          <Typography variant="caption" sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 600, minWidth: 200 }}>
            {actionLabel || 'Traitement en cours…'}
          </Typography>
          {bulkProgress && (
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', whiteSpace: 'nowrap' }}>
              {bulkProgress.current} / {bulkProgress.total}
            </Typography>
          )}
          <Box sx={{ flex: 1 }}>
            <LinearProgress
              variant={bulkProgress ? 'determinate' : 'indeterminate'}
              value={bulkProgress ? Math.min(100, (bulkProgress.current / bulkProgress.total) * 100) : undefined}
              sx={{ height: 6, borderRadius: 3, backgroundColor: 'rgba(255,255,255,0.2)',
                '& .MuiLinearProgress-bar': { backgroundColor: 'white' } }}
            />
          </Box>
        </Box>
      )}

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* KPI */}
      <KpiSection />

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Liste des payouts" icon={<PayoutsIcon />} iconPosition="start" />
          <Tab label="Scheduler" icon={<ScheduleIcon />} iconPosition="start" />
          <Tab label="Analytics" icon={<TimelineIcon />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Tab 0: Liste */}
      {tabValue === 0 && (
        <>
          <PayoutFilterPanel
            filters={filters}
            filterOpen={filterOpen}
            loading={loading}
            actionLoading={actionLoading}
            setFilters={setFilters}
            setFilterOpen={setFilterOpen}
            resetFilters={resetFilters}
            loadPayouts={loadPayouts}
            setPage={setPage}
          />
          <PayoutsTable />
        </>
      )}

      {/* Tab 1: Scheduler */}
      {tabValue === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Alert severity={schedulerStatus.scheduler?.isRunning ? 'success' : 'warning'}>
              <AlertTitle>Scheduler : {schedulerStatus.scheduler?.isRunning ? 'Actif' : 'Inactif'}</AlertTitle>
              {schedulerStatus.scheduler?.isRunning
                ? `Traitement automatique selon la planification (${schedulerStatus.scheduler?.frequency || 'N/A'})`
                : 'Arrêté — les payouts ne sont pas traités automatiquement.'}
            </Alert>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>Contrôle</Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {['start', 'stop', 'restart'].map(action => (
                  <Button key={action} variant={action === 'stop' ? 'contained' : 'outlined'}
                    color={action === 'stop' ? 'error' : action === 'start' ? 'success' : 'primary'}
                    startIcon={action === 'start' ? <StartIcon /> : action === 'stop' ? <StopIcon /> : <RefreshIcon />}
                    onClick={() => handleSchedulerAction(action)} disabled={loading || actionLoading}>
                    {action === 'start' ? 'Démarrer' : action === 'stop' ? 'Arrêter' : 'Redémarrer'}
                  </Button>
                ))}
                <Button variant="outlined" startIcon={<StartIcon />} onClick={handleManualRun} disabled={loading || actionLoading}>
                  Exécution manuelle
                </Button>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>Payouts en attente de traitement</Typography>
              <Typography variant="h3" fontWeight={700} color="warning.main">
                {schedulerStatus.pendingPayouts?.count ?? 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Montant total : {fmt(schedulerStatus.pendingPayouts?.totalAmount)}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Tab 2: Analytics */}
      {tabValue === 2 && <AnalyticsTab />}

      {/* ── Dialogs ──────────────────────────────────────────────────────────── */}

      {/* Confirmation */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, action: '', data: null, note: '' })} maxWidth="sm" fullWidth>
        <DialogTitle>
          {confirmDialog.action === 'cancel' && '🚫 Annuler ce payout ?'}
          {confirmDialog.action === 'force'  && '⚡ Forcer ce payout ?'}
          {confirmDialog.action === 'retry'  && '🔄 Relancer ce payout ?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            {confirmDialog.action === 'force' && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>Attention :</strong> Le transfert Stripe sera déclenché <strong>immédiatement</strong>, même si la date prévue n'est pas atteinte. Cette action est irréversible.
              </Alert>
            )}
            {confirmDialog.action === 'cancel' && (
              <Typography variant="body2" sx={{ mb: 1 }}>Le payout sera annulé. Le propriétaire ne recevra pas ce virement.</Typography>
            )}
            {confirmDialog.action === 'retry' && (
              <Typography variant="body2" sx={{ mb: 1 }}>Le payout sera remis en statut <strong>pending</strong> avec la date prévue mise à <strong>maintenant</strong>. Il sera traité lors du prochain cycle cron.</Typography>
            )}
            {confirmDialog.data && (
              <Box sx={{ mt: 1, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="body2"><strong>Propriétaire :</strong> {confirmDialog.data.owner?.name || confirmDialog.data.owner_id}</Typography>
                <Typography variant="body2"><strong>Montant :</strong> {fmt(confirmDialog.data.net_amount, confirmDialog.data.currency)}</Typography>
                <Typography variant="body2"><strong>Statut actuel :</strong> {confirmDialog.data.status}</Typography>
              </Box>
            )}
          </DialogContentText>
          <TextField
            fullWidth
            required
            multiline
            rows={3}
            label="Note administrative *"
            placeholder="Motif de l'action (obligatoire)..."
            value={confirmDialog.note || ''}
            onChange={(e) => setConfirmDialog(prev => ({ ...prev, note: e.target.value }))}
            sx={{ mt: 2 }}
            error={confirmDialog.open && !confirmDialog.note?.trim()}
            helperText={confirmDialog.open && !confirmDialog.note?.trim() ? 'Ce champ est obligatoire' : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, action: '', data: null, note: '' })}>Annuler</Button>
          <Button
            variant="contained"
            color={confirmDialog.action === 'cancel' ? 'error' : 'primary'}
            disabled={!confirmDialog.note?.trim()}
            onClick={() => { handlePayoutAction(confirmDialog.action, confirmDialog.data.id, confirmDialog.data, confirmDialog.note); setConfirmDialog({ open: false, action: '', data: null, note: '' }); }}>
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Note (actions groupées) */}
      <Dialog open={bulkNoteDialog.open} onClose={() => setBulkNoteDialog({ open: false, action: '', note: '' })} maxWidth="sm" fullWidth>
        <DialogTitle>
          {bulkNoteDialog.action === 'cancel' && `🚫 Annuler ${selectedPayoutIds.size} payout(s) ?`}
          {bulkNoteDialog.action === 'force'  && `⚡ Forcer ${selectedPayoutIds.size} payout(s) ?`}
          {bulkNoteDialog.action === 'retry'  && `🔄 Relancer ${selectedPayoutIds.size} payout(s) ?`}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Une note administrative est obligatoire pour les actions groupées. Elle sera enregistrée sur chaque payout sélectionné.
          </Typography>
          <TextField
            fullWidth
            required
            multiline
            rows={3}
            label="Note administrative *"
            placeholder="Motif de l'action groupée (obligatoire)..."
            value={bulkNoteDialog.note}
            onChange={(e) => setBulkNoteDialog(prev => ({ ...prev, note: e.target.value }))}
            error={!bulkNoteDialog.note.trim()}
            helperText={!bulkNoteDialog.note.trim() ? 'Ce champ est obligatoire' : ''}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkNoteDialog({ open: false, action: '', note: '' })}>Annuler</Button>
          <Button
            variant="contained"
            color={bulkNoteDialog.action === 'cancel' ? 'error' : 'primary'}
            disabled={!bulkNoteDialog.note.trim()}
            onClick={() => {
              const { action, note } = bulkNoteDialog;
              setBulkNoteDialog({ open: false, action: '', note: '' });
              handleBulkAction(action, note);
            }}
          >
            Confirmer ({selectedPayoutIds.size} payout{selectedPayoutIds.size > 1 ? 's' : ''})
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notes */}
      <Dialog open={notesDialog.open} onClose={() => setNotesDialog({ open: false, payout: null, note: '' })} maxWidth="sm" fullWidth>
        <DialogTitle>Ajouter une note administrative</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Payout #{notesDialog.payout?.id?.slice(0, 8)}… — {notesDialog.payout?.owner?.name || ''}
          </Typography>
          {notesDialog.payout?.metadata?.admin_notes && (
            <Alert severity="info" sx={{ mb: 1.5 }}>
              <AlertTitle>Notes existantes</AlertTitle>
              <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                {notesDialog.payout.metadata.admin_notes}
              </Typography>
            </Alert>
          )}
          <TextField
            fullWidth multiline rows={4}
            label="Nouvelle note"
            value={notesDialog.note}
            onChange={e => setNotesDialog(p => ({ ...p, note: e.target.value }))}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotesDialog({ open: false, payout: null, note: '' })}>Annuler</Button>
          <Button variant="contained" onClick={handleAddNotes} disabled={!notesDialog.note.trim()}>Sauvegarder</Button>
        </DialogActions>
      </Dialog>

      {/* Stripe error */}
      <Dialog open={stripeErrorDialog.open} onClose={() => setStripeErrorDialog({ open: false, error: null, payout: null })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: 'error.main', display: 'flex', alignItems: 'center', gap: 1 }}>
          <ErrorIcon /> Compte Stripe Connect non prêt
        </DialogTitle>
        <DialogContent>
          {stripeErrorDialog.error && (
            <Box>
              <Alert severity="error" sx={{ mb: 2 }}>{stripeErrorDialog.error.message}</Alert>
              {stripeErrorDialog.error.actions?.missing_requirements?.length > 0 && (
                <>
                  <Typography variant="subtitle2" gutterBottom>Informations manquantes :</Typography>
                  <Box component="ul" sx={{ pl: 2 }}>
                    {stripeErrorDialog.error.actions.missing_requirements.map((r, i) => (
                      <li key={i}><Typography variant="body2">{r}</Typography></li>
                    ))}
                  </Box>
                </>
              )}
              {stripeErrorDialog.error.actions?.requires_owner_onboarding && (
                <Alert severity="warning">
                  Le propriétaire doit compléter son onboarding Stripe Connect avant de pouvoir recevoir des virements.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStripeErrorDialog({ open: false, error: null, payout: null })}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Box sx={{ position: 'fixed', bottom: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 2000 }}>
        <Collapse in={snackbar.open}>
          <Alert
            severity={snackbar.severity}
            onClose={() => setSnackbar(p => ({ ...p, open: false }))}
            sx={{ minWidth: 300, boxShadow: 6 }}
          >
            {snackbar.message}
          </Alert>
        </Collapse>
      </Box>
    </Box>
  );
};

export default AdminPayoutsPage;
